import { z } from "zod"

const isAbsoluteUrl = (value: string): boolean => z.string().url().safeParse(value).success

const schema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  // PRD-15 — public origin used by the survey email's CTA. Defaults to
  // window.location.origin in dev so onboarding doesn't require an
  // extra .env line; required for Cloud Run (set in the build args).
  VITE_SURVEY_PUBLIC_BASE_URL: z.string().url().optional(),
  // Stamped on feedback reports. Read here and nowhere else
  // (`architecture.md` §Settings / env); empty when the build sets none.
  VITE_APP_VERSION: z.string().default(""),

  // ── PRD-57 / ADR-0046 — PostHog ────────────────────────────────────
  // Every PostHog field uses `.catch()`, not `.default()`.
  //
  //   `.default(x)` only fires when the value is `undefined`.
  //   `.catch(x)`  fires on ANY validation failure, empty string included.
  //
  // That distinction decides whether the app boots. Docker turns a missing
  // GitHub variable into `--build-arg VITE_POSTHOG_ENV=`, which overrides the
  // Dockerfile's ARG default with `""` — a value `.default()` does not cover.
  // This module throws at import time, so a failed parse here means a blank
  // screen with no message. Analytics is optional by design
  // (`VITE_POSTHOG_ENABLED=false` is a supported state); optional config must
  // never be able to take down what is mandatory, so a bad value degrades to
  // "analytics off" instead of aborting the SPA.
  //
  // The `superRefine` below is the deliberate exception: if someone turned
  // analytics ON and forgot the token, that IS a configuration error worth
  // shouting about — it fails on a variable somebody set on purpose, not on
  // one that merely went missing.
  VITE_POSTHOG_PROJECT_TOKEN: z
    .string()
    .refine((value) => value === "" || value.startsWith("phc_"), "Must be a PostHog project token")
    .catch(""),
  // Either an absolute origin or a same-origin path. The path form is the
  // reverse proxy (`/ingest`, see docker/nginx.conf): `us.i.posthog.com` is on
  // the default block list of uBlock Origin, Brave and many school network
  // filters, and a blocked request fails silently — the professor notices
  // nothing and simply never appears in PostHog.
  VITE_POSTHOG_HOST: z
    .string()
    .refine(
      (value) => value.startsWith("/") || isAbsoluteUrl(value),
      "Must be an absolute URL or a same-origin path",
    )
    .catch("/ingest"),
  // `staging` is a real value even though staging normally runs with
  // analytics off: we are on the free plan, which means ONE project and that
  // project is production. If staging is ever switched on, its events land in
  // that same project and `environment` is the only thing separating them.
  // Without `staging` in this enum, sending it would not mislabel data — it
  // would white-screen the app.
  VITE_POSTHOG_ENV: z.enum(["development", "staging", "production"]).catch("development"),
  VITE_POSTHOG_ENABLED: z.preprocess((value) => value === "true", z.boolean()),
})

const parsedSchema = schema.superRefine((values, ctx) => {
  if (values.VITE_POSTHOG_ENABLED && !values.VITE_POSTHOG_PROJECT_TOKEN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["VITE_POSTHOG_PROJECT_TOKEN"],
      message: "Required when VITE_POSTHOG_ENABLED=true",
    })
  }
})

const parsed = parsedSchema.safeParse(import.meta.env)
if (!parsed.success) {
  console.error("Invalid env:", parsed.error.flatten())
  throw new Error("Invalid environment variables. See src/env.ts")
}

export const env = parsed.data
